import React, { useMemo, useState } from 'react';
import { BuildingIcon, PlusIcon, SearchIcon, UserIcon } from 'lucide-react';
import { ProCustomer } from '../../../types/pro';
import { Modal } from '../Modal';
import { contactSearchText, primaryContact } from '../../../utils/contacts';
interface CustomerPickerProps {
  isOpen: boolean;
  onClose: () => void;
  customers: ProCustomer[];
  onSelect: (customer: ProCustomer) => void;
  onCreateNew: () => void;
}
function normalize(value: string): string {
  return value.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa');
}

/**
 * Picking the customer is the first thing you do on an invoice, so it is a
 * search box rather than a dropdown: you type the name, the phone number or
 * the street and press Enter.
 */
export function CustomerPicker({
  isOpen,
  onClose,
  customers,
  onSelect,
  onCreateNew
}: CustomerPickerProps) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => {
    const term = normalize(query.trim());
    const sorted = [...customers].sort((a, b) => a.name.localeCompare(b.name, 'da'));
    if (!term) return sorted;
    return sorted.filter(customer => normalize([customer.name, contactSearchText(customer), customer.customerNo, customer.cvr, customer.phone, customer.email, customer.address, customer.postalCode, customer.city].filter(Boolean).join(' ')).includes(term));
  }, [customers, query]);
  const choose = (customer: ProCustomer) => {
    onSelect(customer);
    setQuery('');
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={onClose} title="Vælg kunde" subtitle="Søg på navn, firma, telefon, e-mail eller adresse" size="md" data-id="element-193" data-mp="809oyah1po0i">
      <div className="relative mb-4" data-id="element-194" data-mp="cx048vl10fkv">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" data-id="element-195" data-mp="hx4o8vl57sfu" />
        <input autoFocus value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter' && results[0]) choose(results[0]);
      }} placeholder="Søg kunde…" aria-label="Søg kunde" className="w-full rounded-lg border border-stone-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-196" data-mp="3ofy4apvgcbsd" />
      </div>

      <button onClick={() => {
      onCreateNew();
      onClose();
    }} className="mb-3 flex w-full items-center gap-3 rounded-lg border border-dashed border-stone-300 px-4 py-3 text-left transition-colors hover:border-brand-400 hover:bg-brand-50" data-id="element-197" data-mp="61ykpq3kvo4v">
        <PlusIcon className="h-4 w-4 text-brand-600" data-id="element-198" data-mp="2mpja7gkv8zgq" />
        <span className="text-sm font-semibold text-brand-700" data-id="element-199" data-mp="2mq2omnbb55q0">
          Opret ny kunde
        </span>
      </button>

      {results.length === 0 ? <p className="py-8 text-center text-sm text-stone-500" data-id="element-200" data-mp="x85l81bkgz60">
          Ingen kunder matcher “{query}”.
        </p> : <ul className="divide-y divide-stone-100 overflow-hidden rounded-lg border border-stone-200" data-id="element-201" data-mp="126gvgd5rxbor">
          {results.map(customer => {
        const Icon = customer.type === 'firma' ? BuildingIcon : UserIcon;
        return <li key={customer.id} data-id="element-202" data-mp="ei7crk3ux41u">
                <button onClick={() => choose(customer)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-stone-50" data-id="element-203" data-mp="eh4l39ypmxkv">
                  <Icon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-204" data-mp="4lbahw4jxe5q" />
                  <span className="min-w-0 flex-1" data-id="element-205" data-mp="9ib6tfbln2jn">
                    <span className="block truncate text-sm font-medium text-stone-900" data-id="element-206" data-mp="9l75x6z6nx9o">
                      {customer.name}
                    </span>
                    <span className="block truncate text-xs text-stone-500" data-id="element-207" data-mp="23w2p137zuuqp">
                      {[primaryContact(customer)?.name, [customer.address, customer.postalCode, customer.city].filter(Boolean).join(' '), customer.phone].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-208" data-mp="3h269ishzp0u5">
                    {customer.customerNo}
                  </span>
                </button>
              </li>;
      })}
        </ul>}
    </Modal>;
}